import { useTranslation } from "react-i18next";
import PageWrapper from "../../components/PageWrapper";
import useLanguage from "../../utils/useLanguage";
import { Link } from "react-router-dom";
import CustomButton from "../../components/CustomButton";
import DashboardSection from "../../components/DasboardSection";
import axiosClient from "../../services/axiosClient";
import React, { useRef, useState } from "react";
import CustomInput from "../../components/CustomInput";
import InformationBox from "../../components/InformationBox";

const HsCodePage = () => {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();

  // Loading states
  const [addLoading, setAddLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [addJSONfileLoading, setAddJSONfileLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  // Input
  const [searchQuery, setSearchQuery] = useState("");
  // Data
  const [hsCodeData, setHsCodeData] = useState([]);
  const [feedback, setFeedback] = useState({
    message: "",
    status: "",
  });

  const addHsCodes = async () => {
    try {
      // Set loading state
      setAddLoading(true);
      setFeedback({
        message: t("dataIsBeingAdded"),
        status: "loading",
      });
      const res = await axiosClient.post(
        `${process.env.REACT_APP_URL}/hsCodes/addCodes`
      );

      // Items added successfully!
      if (res.status === 200) {
        setAddLoading(false);
        setFeedback({
          message: t("dataAddedSuccessfully"),
          status: "success",
        });
      }
    } catch (error) {
      // Handle error
      setAddLoading(false);
      if (error.response.status === 400) {
        setFeedback({
          message: t("dataAlreadyExists"),
          status: "error",
        });
      }
      if (error.response.status === 404) {
        setFeedback({
          message: t("fileNotFound"),
          status: "error",
        });
      }
      if (error.response.status === 500) {
        setFeedback({
          message: t("serverError"),
          status: "error",
        });
      }
    }
  };

  const deleteHsCodes = async () => {
    try {
      setDeleteLoading(true);
      const res = await axiosClient.delete(
        `${process.env.REACT_APP_URL}/hsCodes/deleteAll`
      );
      // Items added successfully!
      if (res.status === 200) {
        setDeleteLoading(false);
        setFeedback({
          message: t("dataDeletedSuccessfully"),
          status: "success",
        });
      }
    } catch (error) {
      // Handle error
      if (error.response.status === 500) {
        setDeleteLoading(false);
        setFeedback({
          message: t("serverError"),
          status: "error",
        });
      }
    }
  };

  const addJSONfile = async (file) => {
    try {
      setAddJSONfileLoading(true);
      console.log(file);
      const formData = new FormData();
      formData.append("file", file);
      const res = await axiosClient.post(
        `${process.env.REACT_APP_URL}/hsCodes/addFile`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      // File added successfully!
      if (res.status === 200) {
        // Reset the values
        setTimeout(() => {
          setFeedback({
            message: t("fileUploadedSuccessfully"),
            status: "success",
          });
          setAddJSONfileLoading(false);
        }, 1500);
      }
    } catch (error) {
      if (error.response.status === 400 || error.response.status === 500) {
        setAddJSONfileLoading(false);
        setFeedback({
          message: t("fileUploadError"),
          status: "error",
        });
      }
    }
  };

  const handleSearch = async () => {
    try {
      setSearchLoading(true);
      const res = await axiosClient.get(
        `${process.env.REACT_APP_URL}/hsCodes/getSpecificCode`,
        {
          params: {
            code: searchQuery,
          },
        }
      );
      if (res.status === 200) {
        console.log(res.data);

        setHsCodeData((prev) => {
          const result = [
            {
              code: res.data?.parent?.code,
              description: res.data?.parent?.description,
            },
            {
              code: res.data?.child1?.code,
              description: res.data?.child1?.description,
            },
            {
              code: res.data?.child2?.code,
              description: res.data?.child2?.description,
            },
            {
              code: res.data?.child3?.code,
              description: res.data?.child3?.description,
            },
          ];
          return result;
        });

        // Reset the values
        setSearchLoading(false);
        setSearchQuery("");
        setFeedback({
          message: "",
          status: "",
        });
      }
    } catch (error) {
      setSearchLoading(false);
      setSearchQuery("");
      console.log(error.response.status);

      if (error.response.status === 400) {
        setFeedback({
          message: t("invalidCode"),
          status: "error",
        });
      }
      if (error.response.status === 404) {
        setFeedback({
          message: t("codeNotFound"),
          status: "error",
        });
      }
      if (error.response.status === 500) {
        setFeedback({
          message: t("serverError"),
          status: "error",
        });
      }
    }
  };
  return (
    <div>
      <PageWrapper
        style={{ direction: currentDirection }}
        title={t("hsCode")}
      >
        {feedback?.status ? (
          <InformationBox>{feedback?.message}</InformationBox>
        ) : null}
        <DashboardSection title={"hsCode"}>
          <div className="o-column o-column--gap-size_600">
            {/* Input and search button */}
            <div className="o-column-row-switcher o-column--gap-size_300">
              <CustomInput
                label={t("hsCode")}
                placeholder={t("hsCode")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                thick
              />
              <CustomButton
                title={t("search")}
                onClick={handleSearch}
                icon='search'
                iconSize={15}
                loading={searchLoading}
                disabled={
                  searchLoading ||
                  addLoading ||
                  deleteLoading ||
                  addJSONfileLoading
                }
              />
            </div>
            {/* Add, delete and upload buttons */}
            <div className="o-column o-column--gap-size_600">
              <CustomButton
                onClick={() => {
                  addHsCodes();
                }}
                title={t("addHsCode")}
                variant="blank"
                elementType={Link}
                loading={addLoading}
                disabled={
                  addLoading ||
                  deleteLoading ||
                  addJSONfileLoading ||
                  searchLoading
                }
              />
              <CustomButton
                onClick={() => {
                  deleteHsCodes();
                }}
                title={t("deleteHsCode")}
                variant="blank"
                elementType={Link}
                loading={deleteLoading}
                disabled={
                  deleteLoading ||
                  addLoading ||
                  addJSONfileLoading ||
                  searchLoading
                }
              />
              <FileUploadButton onFileSelect={(file) => addJSONfile(file)}>
                <CustomButton
                  title={t("addJsonFile")}
                  variant="blank"
                  loading={addJSONfileLoading}
                  disabled={
                    addJSONfileLoading ||
                    addLoading ||
                    deleteLoading ||
                    searchLoading
                  }
                />
              </FileUploadButton>
            </div>
          </div>
        </DashboardSection>
      </PageWrapper>
    </div>
  );
};

export default HsCodePage;

// FileUploadButton component
function FileUploadButton({ onFileSelect, children, ...props }) {
  const fileInputRef = useRef(null);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && onFileSelect) {
      onFileSelect(file);
      event.target.value = "";
    }
  };

  return (
    <>
      {React.cloneElement(children, {
        ...props,
        onClick: handleClick,
      })}

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".json" // Accept only JSON files
        style={{ display: "none" }}
      />
    </>
  );
}
